/**
 * 1-based → 0-based 轉換工具組
 *
 * 適用場景：
 *  - visibleIfConditionsInit  → convertVisibleIf({ q10: '{q9} = 1' })
 *  - triggersConditionsInit   → convertTriggers([{ type:'complete', expression:'{q1} = 2' }])
 *  - questionMappings         → convertQuestionMappings({ q21: 'q22' })
 *  - 獨立 expression 字串     → convertExpression('{q78.0} = 1 or ...')
 *  - JS 程式碼 (endSurveyMsg) → survey.getValue(q(1)) === v(2)
 */

/**
 * 共用：把一段 expression 字串裡的 q 序號和比較值都 -1
 * {q9} → {q8},  = 1 → = 0
 * 也可直接 export 給獨立的 expression 變數使用
 */
export function convertExpression(str) {
  // 1. Decrement {qN} and {qN.M} references (matrix row refs like {q77.0})
  //    Both question number N and row index M are decremented
  let result = str.replace(/\{q(\d+)((?:\.\d+)*)\}/g, (_, n, suffix) => {
    const newSuffix = suffix.replace(/\.(\d+)/g, (_, m) => `.${parseInt(m, 10) - 1}`)
    return `{q${parseInt(n, 10) - 1}${newSuffix}}`
  })

  // 2. Decrement integers inside array brackets [ ... ]
  //    e.g. anyof [1, 2, 3, "other"] → anyof [0, 1, 2, "other"]
  //    \b(\d+)\b matches standalone numbers; quoted strings like "other" have no digits so are unaffected
  result = result.replace(/\[([^\]]*)\]/g, (_, inner) => {
    const converted = inner.replace(
      /\b(\d+)\b/g,
      (_, n) => `${parseInt(n, 10) - 1}`
    )
    return `[${converted}]`
  })

  // 3. Decrement numeric values after comparison operators: = 1 → = 0
  //    Does not affect string values like = "other"
  result = result.replace(
    /((?:=|<>|!=|>=|<=|>|<)\s*)(\d+)/g,
    (_, op, n) => `${op}${parseInt(n, 10) - 1}`
  )

  return result
}

/**
 * visibleIfConditionsInit 轉換
 * key 的 q 序號也 -1
 *
 * Example:
 *   input:  { q10: '{q9} = 1' }
 *   output: { q9:  '{q8} = 0' }
 */
export function convertVisibleIf(conditions) {
  if (!conditions) return conditions

  const result = {}
  for (const [key, value] of Object.entries(conditions)) {
    // Handles: 'q7' → 'q6', 'q22.row1' → 'q21.row0'
    // Both question number and .rowN index are decremented
    const newKey = key
      .replace(/^q(\d+)/, (_, n) => `q${parseInt(n, 10) - 1}`)
      .replace(/\.row(\d+)/, (_, n) => `.row${parseInt(n, 10) - 1}`)
    result[newKey] = convertExpression(value)
  }
  return result
}

/**
 * triggersConditionsInit 轉換
 * 只轉每個 trigger 的 expression 欄位，其餘欄位不動
 *
 * Example:
 *   input:  [{ type: 'complete', expression: '{q1} = 2' }]
 *   output: [{ type: 'complete', expression: '{q0} = 1' }]
 */
export function convertTriggers(triggers) {
  if (!triggers) return triggers
  return triggers.map((trigger) =>
    trigger.expression
      ? { ...trigger, expression: convertExpression(trigger.expression) }
      : trigger
  )
}

/**
 * questionMappings 轉換
 * key 與 value 都是題目名稱，兩者的 q 序號都 -1
 *
 * Example:
 *   input:  { q21: 'q22' }
 *   output: { q20: 'q21' }
 */
export function convertQuestionMappings(mappings) {
  if (!mappings) return mappings
  const decrement = (s) =>
    s.replace(/^q(\d+)/, (_, n) => `q${parseInt(n, 10) - 1}`)
  const result = {}
  for (const [key, value] of Object.entries(mappings)) {
    result[decrement(key)] = decrement(value)
  }
  return result
}

/**
 * JS 程式碼用：把 1-based 題號轉成 0-based 字串
 * 用於 survey.getValue(q(1))  →  survey.getValue('q0')
 */
export function q(n) {
  return `q${n - 1}`
}

/**
 * JS 程式碼用：把 1-based 選項值轉成 0-based 數字
 * 用於 survey.getValue(q(1)) === v(2)  →  === 1
 */
export function v(n) {
  return n - 1
}
